import type { RequestBody, ResultScore, ScoreInput } from "@repo/shared-types";
import type { Read } from "./event-form";

/**
 * How a score of each measure is typed, and read back into its boxes.
 *
 * Nobody types a unit or a format. A measure has a box for each of its
 * numbers — minutes and seconds, rounds and reps, feet and inches — and, where
 * it has one, a unit picked from a list beside them. This turns the boxes into
 * the base units the api takes (milliseconds, grams, millimetres) and back,
 * for an edit form. How a score reads everywhere else is the api's `display`.
 *
 * Like the api, nothing here knows a score type by its label: only what it
 * measures and which way is better. The api checks everything again; checking
 * here is only so a message can name the part the way the form does.
 */

/** What reading a score needs to know about its type. */
export type Measured = { label: string; measure: string; better: string };

/**
 * A distance keeps the unit it was typed in, and everyone reads it in that
 * unit. The app is metric only since 19 October: a distance is typed in
 * METRIC_DISTANCE_UNITS, and one typed before in mi, yd, ft or in still reads
 * in it and keeps it when edited. A load is kg.
 */
export const DISTANCE_UNITS = ["m", "km", "cm", "mi", "yd", "ft", "in"] as const;
export type DistanceUnit = (typeof DISTANCE_UNITS)[number];
export const METRIC_DISTANCE_UNITS: readonly DistanceUnit[] = ["m", "km", "cm"];

const GRAMS_PER_KG = 1_000;

const MILLIMETRES_PER: Record<DistanceUnit, number> = {
  m: 1_000,
  km: 1_000_000,
  cm: 10,
  mi: 1_609_344,
  yd: 914.4,
  ft: 304.8,
  in: 25.4,
};

/**
 * How many decimals each unit is typed and shown to — the same as the api
 * shows it, so a number reads back the way it was typed. Feet take whole
 * feet and a box for the inches.
 */
const DECIMALS: Record<"kg" | DistanceUnit, number> = {
  kg: 2,
  m: 2,
  km: 3,
  cm: 1,
  mi: 3,
  yd: 1,
  ft: 0,
  in: 1,
};

// The api's sanity bounds, so a typo is caught here with a friendlier message.
const MAX_MS = 24 * 60 * 60 * 1_000;
const MAX_GRAMS = 1_000_000;
const MAX_COUNT = 100_000;
const MAX_ROUNDS = 10_000;
const MAX_MM = 1_000_000_000;

/**
 * Whether a score can hit a cap: the first score of a part raced against the
 * clock, when the part has a time limit — which is then the cap.
 */
export function racesTheCap(scoreType: Measured, position: number, timeLimitMs: number | null): boolean {
  return position === 0 && scoreType.measure === "time" && scoreType.better === "lower" && timeLimitMs !== null;
}

type Parsed = { value: number } | { error: string } | { empty: true };

/**
 * A number as somebody typed it, "102.5" or "102,5" — a decimal comma is what
 * a phone set to Portuguese offers — with at most `decimals` places.
 */
function parseNumber(text: string, decimals: number, what: string): Parsed {
  const typed = text.trim();
  if (typed === "") return { empty: true };
  const match = /^(\d+)(?:[.,](\d+))?$/.exec(typed);
  if (!match) return { error: `${what} is not a number.` };
  const places = match[2]?.length ?? 0;
  if (places > decimals) {
    return {
      error: decimals === 0 ? `${what} is a whole number.` : `${what} takes at most ${decimals} decimal${decimals === 1 ? "" : "s"}.`,
    };
  }
  return { value: Number(`${match[1]}.${match[2] ?? "0"}`) };
}

const text = (form: FormData, name: string) => String(form.get(name) ?? "");

/**
 * Reads one score's boxes, named `${prefix}.min`, `${prefix}.amount` and so on,
 * into what the api takes. `{}` is a score left empty. `name` is how the form
 * calls the part, for messages: "Part A".
 */
export function readScore(
  form: FormData,
  prefix: string,
  scoreType: Measured,
  name: string,
): Read<RequestBody<ScoreInput>> {
  const field = (suffix: string) => text(form, `${prefix}.${suffix}`);
  // The score's boxes are the one place to change: a message names them.
  const fail = (message: string) => ({ error: `${name}: ${message}`, field: prefix });

  switch (scoreType.measure) {
    case "time": {
      if (field("capped") !== "") {
        const reps = parseNumber(field("reps"), 0, "The reps done");
        if ("error" in reps) return fail(reps.error);
        if ("empty" in reps) return fail("say how many reps were done by the cap.");
        if (reps.value > MAX_COUNT) return fail("that is more reps than anybody does.");
        return { value: { capped: true, reps: reps.value } };
      }
      const minutes = parseNumber(field("min"), 0, "The minutes");
      const seconds = parseNumber(field("sec"), 2, "The seconds");
      if ("error" in minutes) return fail(minutes.error);
      if ("error" in seconds) return fail(seconds.error);
      if ("empty" in minutes && "empty" in seconds) return { value: {} };
      const min = "value" in minutes ? minutes.value : 0;
      const sec = "value" in seconds ? seconds.value : 0;
      if (sec >= 60) return fail("the seconds go up to 59 — put whole minutes in the minutes.");
      const ms = Math.round((min * 60 + sec) * 1_000);
      if (ms <= 0) return fail("a time is more than 0:00.");
      if (ms > MAX_MS) return fail("a time is at most 24 hours.");
      return { value: { value: ms } };
    }

    case "rounds_reps": {
      const rounds = parseNumber(field("rounds"), 0, "The rounds");
      const reps = parseNumber(field("reps"), 0, "The reps");
      if ("error" in rounds) return fail(rounds.error);
      if ("error" in reps) return fail(reps.error);
      if ("empty" in rounds && "empty" in reps) return { value: {} };
      const value = "value" in rounds ? rounds.value : 0;
      const extra = "value" in reps ? reps.value : 0;
      if (value > MAX_ROUNDS || extra > MAX_COUNT) return fail("that is more than anybody does.");
      return { value: { value, reps: extra } };
    }

    case "load": {
      const amount = parseNumber(field("amount"), DECIMALS.kg, "The load");
      if ("error" in amount) return fail(amount.error);
      if ("empty" in amount) return { value: {} };
      const grams = Math.round(amount.value * GRAMS_PER_KG);
      if (grams <= 0) return fail("a load is more than 0.");
      if (grams > MAX_GRAMS) return fail("a load is at most 1,000 kg.");
      return { value: { value: grams } };
    }

    case "count": {
      const count = parseNumber(field("amount"), 0, "The count");
      if ("error" in count) return fail(count.error);
      if ("empty" in count) return { value: {} };
      if (count.value > MAX_COUNT) return fail("that is more than anybody does.");
      return { value: { value: count.value } };
    }

    case "distance": {
      const unit = field("unit") as DistanceUnit;
      if (!DISTANCE_UNITS.includes(unit)) return fail("pick the unit the distance is in.");
      const amount = parseNumber(field("amount"), DECIMALS[unit], unit === "ft" ? "The feet" : "The distance");
      if ("error" in amount) return fail(amount.error);
      let total = "value" in amount ? amount.value : 0;
      if (unit === "ft") {
        const inches = parseNumber(field("inches"), 1, "The inches");
        if ("error" in inches) return fail(inches.error);
        if ("empty" in amount && "empty" in inches) return { value: {} };
        if ("value" in inches) {
          if (inches.value >= 12) return fail("the inches go up to 11.9 — put whole feet in the feet.");
          total += inches.value / 12;
        }
      } else if ("empty" in amount) {
        return { value: {} };
      }
      const mm = Math.round(total * MILLIMETRES_PER[unit]);
      if (mm <= 0) return fail("a distance is more than 0.");
      if (mm > MAX_MM) return fail("a distance is at most 1,000 km.");
      return { value: { value: mm, unit } };
    }

    case "pass_fail": {
      const result = field("result");
      if (result === "") return { value: {} };
      return { value: { value: result === "pass" ? 1 : 0 } };
    }

    default:
      // Not scored, or a measure this build does not know: nothing to type.
      return { value: {} };
  }
}

/** A number with at most `decimals` places, and none it does not need. */
function trimmed(value: number, decimals: number): string {
  return String(Number(value.toFixed(decimals)));
}

/**
 * A recorded score as its boxes show it, for an edit form: each box's suffix
 * and what it holds. A load reads in kg; a distance in the unit it was typed
 * in. A score not recorded fills no box.
 */
export function scoreFields(score: ResultScore): Record<string, string> {
  if (score.value === null) return {};
  const value = score.value;

  switch (score.measure) {
    case "time": {
      if (score.capped) return { capped: "on", reps: String(score.reps ?? 0) };
      return { min: String(Math.floor(value / 60_000)), sec: trimmed((value % 60_000) / 1_000, 2) };
    }
    case "rounds_reps":
      return { rounds: String(value), reps: String(score.reps ?? 0) };
    case "load":
      return { amount: trimmed(value / GRAMS_PER_KG, DECIMALS.kg) };
    case "count":
      return { amount: String(value) };
    case "distance": {
      const unit = (DISTANCE_UNITS as readonly string[]).includes(score.unit ?? "")
        ? (score.unit as DistanceUnit)
        : "m";
      if (unit === "ft") {
        const tenths = Math.round((value / MILLIMETRES_PER.in) * 10);
        return { amount: String(Math.floor(tenths / 120)), inches: trimmed((tenths % 120) / 10, 1), unit };
      }
      return { amount: trimmed(value / MILLIMETRES_PER[unit], DECIMALS[unit]), unit };
    }
    case "pass_fail":
      return { result: value === 1 ? "pass" : "fail" };
    default:
      return {};
  }
}

/** "1 round", "5 rounds", as the api counts. */
function counted(n: number, noun: string): string {
  return n === 1 ? `1 ${noun}` : `${n} ${noun}s`;
}

/** A time the way a clock reads it, as the api does: "4:12", "1:02:03", "1:32.4". */
function clock(ms: number): string {
  const seconds = Math.floor(ms / 1_000);
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  let shown = hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
  const fraction = ms % 1_000;
  if (fraction > 0) shown += `.${String(fraction).padStart(3, "0").replace(/0+$/, "")}`;
  return shown;
}

/**
 * A score as it will read once saved — the api's `display`, worked out here
 * for a result typed in a dialog and kept on the page until it is sent. A
 * load reads in kg, a distance in the unit it was typed in. Nothing when
 * nothing is recorded.
 */
export function scoreDisplay(score: Pick<ResultScore, "measure" | "value" | "reps" | "capped" | "unit">): string {
  if (score.value === null && !score.capped) return "";
  const value = score.value ?? 0;
  switch (score.measure) {
    case "time":
      return score.capped ? `Capped, ${counted(score.reps ?? 0, "rep")}` : clock(value);
    case "rounds_reps":
      return (score.reps ?? 0) > 0 ? `${counted(value, "round")} + ${counted(score.reps ?? 0, "rep")}` : counted(value, "round");
    case "load":
      return `${trimmed(value / GRAMS_PER_KG, DECIMALS.kg)} kg`;
    case "count":
      return String(value);
    case "distance": {
      const unit = (DISTANCE_UNITS as readonly string[]).includes(score.unit ?? "") ? (score.unit as DistanceUnit) : "m";
      if (unit === "ft") {
        const tenths = Math.round((value / MILLIMETRES_PER.in) * 10);
        const feet = Math.floor(tenths / 120);
        const inches = trimmed((tenths % 120) / 10, 1);
        if (feet === 0) return `${inches} in`;
        return inches === "0" ? `${feet} ft` : `${feet} ft ${inches} in`;
      }
      return `${trimmed(value / MILLIMETRES_PER[unit], DECIMALS[unit])} ${unit}`;
    }
    case "pass_fail":
      return value === 1 ? "Pass" : "Fail";
    default:
      return "";
  }
}
